import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-browser';
import { config } from './config';
import { awsCredentials } from './credentials';
import { Upload, PublicWatchPayload, PublicFeedPayload, SharedVideo, Playlist, CaptionStatus } from './types';

/**
 * POST /uploads/{uploadId}/refresh
 * IAM-authorized. Signed with the caller's Cognito Identity Pool temp credentials.
 */
export async function refreshUpload(uploadId: string): Promise<Upload> {
  const url = new URL(`${trimSlash(config.apiBaseUrl)}/uploads/${encodeURIComponent(uploadId)}/refresh`);
  const signed = await signRequest({
    method: 'POST',
    url,
    body: '',
  });
  const resp = await fetch(signed.url, { method: signed.method, headers: signed.headers, body: signed.body });
  if (resp.status === 403) throw new ApiError('forbidden', 403);
  if (resp.status === 404) throw new ApiError('not_found', 404);
  if (!resp.ok) throw new ApiError(`refresh failed: ${resp.status}`, resp.status);
  return (await resp.json()) as Upload;
}

/**
 * POST /uploads/{uploadId}/publish
 * IAM-authorized. Adds the caller's own ready upload to the org-wide shared library.
 */
export async function publishUpload(uploadId: string): Promise<SharedVideo> {
  const url = new URL(`${trimSlash(config.apiBaseUrl)}/uploads/${encodeURIComponent(uploadId)}/publish`);
  const signed = await signRequest({
    method: 'POST',
    url,
    body: '',
  });
  const resp = await fetch(signed.url, { method: signed.method, headers: signed.headers, body: signed.body });
  if (resp.status === 403) throw new ApiError('forbidden', 403);
  if (resp.status === 404) throw new ApiError('not_found', 404);
  if (resp.status === 409) throw new ApiError('not_ready', 409);
  if (!resp.ok) throw new ApiError(`publish failed: ${resp.status}`, resp.status);
  return (await resp.json()) as SharedVideo;
}

export interface EarlyDeletionFeeRisk {
  storageClass: string;
  eligibleAt: string;
}

/**
 * DELETE /uploads/{uploadId}
 * IAM-authorized. Blocked (409) while the upload is still shared — must unshare first. Also
 * blocked (409) if the source file has already been archived by the VOD pipeline's own lifecycle
 * rules and deleting it now would trigger an S3 early-deletion fee — see delete/index.ts.
 */
export async function deleteUpload(uploadId: string): Promise<void> {
  const url = new URL(`${trimSlash(config.apiBaseUrl)}/uploads/${encodeURIComponent(uploadId)}`);
  const signed = await signRequest({ method: 'DELETE', url, body: '' });
  const resp = await fetch(signed.url, { method: signed.method, headers: signed.headers, body: signed.body });
  if (resp.status === 403) throw new ApiError('forbidden', 403);
  if (resp.status === 404) throw new ApiError('not_found', 404);
  if (resp.status === 409) {
    const body = await resp.json().catch(() => ({}) as Record<string, unknown>);
    if (body.error === 'early_deletion_fee_risk') {
      throw new ApiError('early_deletion_fee_risk', 409, {
        storageClass: body.storageClass,
        eligibleAt: body.eligibleAt,
      } as EarlyDeletionFeeRisk);
    }
    throw new ApiError('still_shared', 409);
  }
  if (!resp.ok) throw new ApiError(`delete failed: ${resp.status}`, resp.status);
}

export interface UploadMetadataPatch {
  filename?: string;
  description?: string;
}

/**
 * POST /uploads/{uploadId}/rename
 * IAM-authorized. Updates the filename and/or description; if the upload is currently published,
 * the shared copy's matching fields are kept in sync server-side (see rename/index.ts) — the
 * client doesn't need to know or care whether it's shared.
 */
export async function updateUpload(uploadId: string, patch: UploadMetadataPatch): Promise<Upload> {
  const url = new URL(`${trimSlash(config.apiBaseUrl)}/uploads/${encodeURIComponent(uploadId)}/rename`);
  const signed = await signRequest({ method: 'POST', url, body: JSON.stringify(patch) });
  const resp = await fetch(signed.url, { method: signed.method, headers: signed.headers, body: signed.body });
  if (resp.status === 403) throw new ApiError('forbidden', 403);
  if (resp.status === 404) throw new ApiError('not_found', 404);
  if (resp.status === 400) throw new ApiError('invalid_filename', 400);
  if (!resp.ok) throw new ApiError(`update failed: ${resp.status}`, resp.status);
  return (await resp.json()) as Upload;
}

/**
 * POST /uploads/{uploadId}/thumbnail
 * IAM-authorized. Sets a custom thumbnail (a CloudFront `/thumbnails/...` URL, already uploaded
 * directly to S3 by the caller); if the upload is currently published, the shared copy's
 * thumbnail is kept in sync server-side (see set-thumbnail/index.ts) — the client doesn't need to
 * know or care whether it's shared.
 */
export async function setThumbnail(uploadId: string, thumbnailUrl: string): Promise<Upload> {
  const url = new URL(`${trimSlash(config.apiBaseUrl)}/uploads/${encodeURIComponent(uploadId)}/thumbnail`);
  const signed = await signRequest({ method: 'POST', url, body: JSON.stringify({ thumbnailUrl }) });
  const resp = await fetch(signed.url, { method: signed.method, headers: signed.headers, body: signed.body });
  if (resp.status === 403) throw new ApiError('forbidden', 403);
  if (resp.status === 404) throw new ApiError('not_found', 404);
  if (resp.status === 400) throw new ApiError('invalid_thumbnail_url', 400);
  if (!resp.ok) throw new ApiError(`set thumbnail failed: ${resp.status}`, resp.status);
  return (await resp.json()) as Upload;
}

/**
 * POST /uploads/{uploadId}/caption
 * IAM-authorized. Sets a caption track (a CloudFront `/captions/...` URL, already uploaded
 * directly to S3 by the caller); replaces any existing (auto-generated or owner-supplied) track.
 * If the upload is currently published, the shared copy's caption track is kept in sync
 * server-side (see set-caption/index.ts) — the client doesn't need to know or care whether it's
 * shared.
 */
export async function setCaption(uploadId: string, captionUrl: string): Promise<Upload> {
  const url = new URL(`${trimSlash(config.apiBaseUrl)}/uploads/${encodeURIComponent(uploadId)}/caption`);
  const signed = await signRequest({ method: 'POST', url, body: JSON.stringify({ captionUrl }) });
  const resp = await fetch(signed.url, { method: signed.method, headers: signed.headers, body: signed.body });
  if (resp.status === 403) throw new ApiError('forbidden', 403);
  if (resp.status === 404) throw new ApiError('not_found', 404);
  if (resp.status === 400) throw new ApiError('invalid_caption_url', 400);
  if (!resp.ok) throw new ApiError(`set caption failed: ${resp.status}`, resp.status);
  return (await resp.json()) as Upload;
}

export interface SharedVideoMetadataPatch {
  title?: string;
  description?: string;
}

/**
 * POST /shared/{videoId}/edit
 * IAM-authorized. Any signed-in user may edit a shared video's title/description — most shared
 * videos (backfilled or bulk-imported) have no real owner in this app, so there's no ownership
 * check to enforce here, the same permission model as unshareVideo. If the video is linked to a
 * personal upload, that copy's filename/description is kept in sync server-side too (see
 * edit-shared/index.ts) — the client doesn't need to know or care whether one exists.
 */
export async function editSharedVideo(
  videoId: string,
  patch: SharedVideoMetadataPatch,
): Promise<SharedVideo> {
  const url = new URL(`${trimSlash(config.apiBaseUrl)}/shared/${encodeURIComponent(videoId)}/edit`);
  const signed = await signRequest({ method: 'POST', url, body: JSON.stringify(patch) });
  const resp = await fetch(signed.url, { method: signed.method, headers: signed.headers, body: signed.body });
  if (resp.status === 403) throw new ApiError('forbidden', 403);
  if (resp.status === 404) throw new ApiError('not_found', 404);
  if (resp.status === 400) throw new ApiError('invalid_title', 400);
  if (!resp.ok) throw new ApiError(`edit failed: ${resp.status}`, resp.status);
  return (await resp.json()) as SharedVideo;
}

/**
 * POST /shared/{videoId}/thumbnail
 * IAM-authorized. Any signed-in user may set a shared video's thumbnail — same permission model as
 * editSharedVideo/unshareVideo — regardless of who published it. If the video is linked to a
 * personal upload, that copy's thumbnail is kept in sync server-side (see
 * set-shared-thumbnail/index.ts) — the client doesn't need to know or care whether one exists.
 */
export async function setSharedThumbnail(videoId: string, thumbnailUrl: string): Promise<SharedVideo> {
  const url = new URL(`${trimSlash(config.apiBaseUrl)}/shared/${encodeURIComponent(videoId)}/thumbnail`);
  const signed = await signRequest({ method: 'POST', url, body: JSON.stringify({ thumbnailUrl }) });
  const resp = await fetch(signed.url, { method: signed.method, headers: signed.headers, body: signed.body });
  if (resp.status === 403) throw new ApiError('forbidden', 403);
  if (resp.status === 404) throw new ApiError('not_found', 404);
  if (resp.status === 400) throw new ApiError('invalid_thumbnail_url', 400);
  if (!resp.ok) throw new ApiError(`set shared thumbnail failed: ${resp.status}`, resp.status);
  return (await resp.json()) as SharedVideo;
}

/** What the shared-captions endpoints report back; the item itself is re-read from DynamoDB. */
export interface SharedCaptionState {
  videoId: string;
  captionStatus: CaptionStatus;
  captionUrl?: string;
  captionSource?: 'auto' | 'owner';
}

/**
 * POST /shared/{videoId}/captions/generate
 * IAM-authorized; any signed-in user. Starts server-side caption generation for a shared video
 * (extract its audio, then transcribe it) — see shared-captions/index.ts. Returns quickly with
 * `generating`; progress is advanced by polling `refreshSharedCaptions`.
 */
export async function generateSharedCaptions(videoId: string): Promise<SharedCaptionState> {
  return postSharedCaptions(videoId, 'generate');
}

/** POST /shared/{videoId}/captions/refresh — advances an in-flight generation one step. */
export async function refreshSharedCaptions(videoId: string): Promise<SharedCaptionState> {
  return postSharedCaptions(videoId, 'refresh');
}

async function postSharedCaptions(
  videoId: string,
  action: 'generate' | 'refresh',
): Promise<SharedCaptionState> {
  const url = new URL(
    `${trimSlash(config.apiBaseUrl)}/shared/${encodeURIComponent(videoId)}/captions/${action}`,
  );
  const signed = await signRequest({ method: 'POST', url, body: '' });
  const resp = await fetch(signed.url, { method: signed.method, headers: signed.headers, body: signed.body });
  if (resp.status === 403) throw new ApiError('forbidden', 403);
  if (resp.status === 404) throw new ApiError('not_found', 404);
  if (resp.status === 409) throw new ApiError('conflict', 409);
  if (!resp.ok) throw new ApiError(`shared captions ${action} failed: ${resp.status}`, resp.status);
  return (await resp.json()) as SharedCaptionState;
}

/**
 * POST /shared/{videoId}/caption
 * IAM-authorized; any signed-in user. Sets a caption track (a CloudFront `/captions/...` URL,
 * already uploaded directly to S3 by the caller) on a shared video, replacing any existing one.
 * Works for backfilled videos with no owning upload — see set-shared-caption/index.ts.
 */
export async function setSharedCaption(videoId: string, captionUrl: string): Promise<SharedCaptionState> {
  const url = new URL(`${trimSlash(config.apiBaseUrl)}/shared/${encodeURIComponent(videoId)}/caption`);
  const signed = await signRequest({ method: 'POST', url, body: JSON.stringify({ captionUrl }) });
  const resp = await fetch(signed.url, { method: signed.method, headers: signed.headers, body: signed.body });
  if (resp.status === 403) throw new ApiError('forbidden', 403);
  if (resp.status === 404) throw new ApiError('not_found', 404);
  if (resp.status === 400) throw new ApiError('invalid_caption_url', 400);
  if (!resp.ok) throw new ApiError(`set shared caption failed: ${resp.status}`, resp.status);
  return (await resp.json()) as SharedCaptionState;
}

export interface SharedPlaylistPatch {
  name?: string;
  addVideoId?: string;
  removeVideoId?: string;
  reorderVideoIds?: string[];
}

/**
 * POST /playlists/{ownerId}/{playlistId}/edit
 * IAM-authorized. Any signed-in user may rename, or add/remove a video on, a playlist that is
 * currently visibility === 'shared' — most shared playlists (e.g. the Wistia bulk import) have no
 * real owner in this app, so the leading-keys-scoped direct writes private playlists use
 * (playlists.ts) can never succeed for them. Rejected with 403 if the playlist isn't shared (see
 * edit-playlist/index.ts) — private playlists keep using the direct client functions in
 * playlists.ts instead, which only ever work for the real owner anyway.
 */
export async function editSharedPlaylist(
  ownerId: string,
  playlistId: string,
  patch: SharedPlaylistPatch,
): Promise<Playlist> {
  const url = new URL(
    `${trimSlash(config.apiBaseUrl)}/playlists/${encodeURIComponent(ownerId)}/${encodeURIComponent(playlistId)}/edit`,
  );
  const signed = await signRequest({ method: 'POST', url, body: JSON.stringify(patch) });
  const resp = await fetch(signed.url, { method: signed.method, headers: signed.headers, body: signed.body });
  if (resp.status === 403) {
    const body = await resp.json().catch(() => ({}) as Record<string, unknown>);
    throw new ApiError(body.error === 'not_shared' ? 'not_shared' : 'forbidden', 403);
  }
  if (resp.status === 404) throw new ApiError('not_found', 404);
  if (resp.status === 409) throw new ApiError('video_not_shared', 409);
  if (resp.status === 400) throw new ApiError('invalid_body', 400);
  if (!resp.ok) throw new ApiError(`edit failed: ${resp.status}`, resp.status);
  return (await resp.json()) as Playlist;
}

/**
 * GET /watch/{uploadId} — public. No credentials, no signing.
 * Returns { hlsUrl, filename } on 200, identical 404 shape for not-ready / not-found.
 */
export async function fetchPublicWatch(uploadId: string): Promise<PublicWatchPayload | null> {
  const url = `${trimSlash(config.apiBaseUrl)}/watch/${encodeURIComponent(uploadId)}`;
  const resp = await fetch(url, { method: 'GET' });
  if (resp.status === 404) return null;
  if (resp.status === 429) throw new ApiError('rate_limited', 429);
  if (!resp.ok) return null;
  return (await resp.json()) as PublicWatchPayload;
}

/**
 * GET /feed/{playlistId} — public. No credentials, no signing.
 * Returns { name, videoIds } on 200, identical 404 shape for unknown playlists.
 */
export async function fetchPublicFeed(playlistId: string): Promise<PublicFeedPayload | null> {
  const url = `${trimSlash(config.apiBaseUrl)}/feed/${encodeURIComponent(playlistId)}`;
  const resp = await fetch(url, { method: 'GET' });
  if (resp.status === 404) return null;
  if (resp.status === 429) throw new ApiError('rate_limited', 429);
  if (!resp.ok) return null;
  return (await resp.json()) as PublicFeedPayload;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

interface SignedRequest {
  method: string;
  url: string;
  headers: Record<string, string>;
  body?: string;
}

async function signRequest({
  method,
  url,
  body,
}: {
  method: string;
  url: URL;
  body: string;
}): Promise<SignedRequest> {
  const signer = new SignatureV4({
    region: config.awsRegion,
    service: 'execute-api',
    sha256: Sha256,
    credentials: awsCredentials,
  });
  const request = new HttpRequest({
    method,
    protocol: url.protocol,
    hostname: url.hostname,
    port: url.port ? Number(url.port) : undefined,
    path: url.pathname,
    query: Object.fromEntries(url.searchParams.entries()),
    headers: {
      host: url.hostname,
      'content-type': 'application/json',
    },
    body,
  });
  const signed = await signer.sign(request);
  return {
    method: signed.method,
    url: url.toString(),
    headers: signed.headers as Record<string, string>,
    body,
  };
}

function trimSlash(s: string): string {
  return s.endsWith('/') ? s.slice(0, -1) : s;
}
